import { selectionCoverage } from '@/components/ui/SelectionLabel'

// The timeline mark (Sam, 2026-11-01; the concept's over-time mark the same
// day): how a list row shows something ONGOING, a storyline or a topic,
// where a story's row (LeanMark) shows a moment. One column a day:
// right-leaning articles rise above an ink line, left-leaning ones hang
// below it, center straddles it. Under it, as on LeanMark, Pano's label
// alone: in a row the label is the answer (Sam, 2026-11-01); the counts are
// on the storyline's or topic's page.
//
// `days`: oldest first, each { left, center, right } (with an optional
// `day`) or [left, center, right]. More than MAX_COLUMNS days are summed
// into weeks, newest week whole, so a three-month row stays readable.
// `counts` gives the label when `label` doesn't; `label={false}` drops it.
// `unit="week"` when the caller has already summed the days into weeks.

const MAX_COLUMNS = 31

const triple = (d) =>
  Array.isArray(d)
    ? [d[0] || 0, d[1] || 0, d[2] || 0]
    : [d.left || 0, d.center || 0, d.right || 0]

function bucket(days) {
  if (days.length <= MAX_COLUMNS) return days
  const out = []
  for (let end = days.length; end > 0; end -= 7)
    out.unshift(
      days
        .slice(Math.max(0, end - 7), end)
        .reduce((s, d) => [s[0] + d[0], s[1] + d[1], s[2] + d[2]], [0, 0, 0])
    )
  return out
}

export default function LeanTimeline({
  days = [],
  counts,
  label,
  height = 15,
  unit = 'day',
  className = '',
}) {
  if (!days.length) return null
  const all = days.map(triple)
  const cols = bucket(all)
  const sum = (i) => all.reduce((n, d) => n + d[i], 0)
  const [left, center, right] = [sum(0), sum(1), sum(2)]
  const text =
    label === false
      ? null
      : (label ??
        selectionCoverage({ lean: counts || { left, center, right } })?.text ??
        null)
  const peak = Math.max(1, ...cols.map((d) => Math.max(d[0], d[2]) + d[1] / 2))
  const h = (n) => `${((n / peak) * height).toFixed(1)}px`
  const gap = cols.length > 14 ? 'gap-px' : 'gap-[3px]'

  return (
    <span className={`block ${className}`}>
      <span className="flex items-center">
        <span
          role="img"
          aria-label={`${unit === 'week' || cols.length !== all.length ? `Last ${cols.length} weeks` : `Last ${all.length} days`}: ${left} left-leaning, ${center} center and ${right} right-leaning articles${text ? `; Pano rates it ${text}` : ''}`}
          className={`flex min-w-0 flex-1 ${gap}`}
        >
          {cols.map((d, i) => (
            <span key={i} className="flex min-w-0 flex-1 flex-col">
              <span
                className="flex flex-col justify-end"
                style={{ height: `${height}px` }}
              >
                <span className="bg-red-600" style={{ height: h(d[2]) }} />
                <span className="bg-zinc-500" style={{ height: h(d[1] / 2) }} />
              </span>
              <span className="h-px bg-foreground" />
              <span
                className="flex flex-col justify-start"
                style={{ height: `${height}px` }}
              >
                <span className="bg-zinc-500" style={{ height: h(d[1] / 2) }} />
                <span className="bg-blue-600" style={{ height: h(d[0]) }} />
              </span>
            </span>
          ))}
        </span>
      </span>
      {text && (
        <span
          className="mt-[5px] block font-sans text-[11px] font-semibold leading-none text-foreground sm:text-xs"
          aria-hidden="true"
        >
          {text}
        </span>
      )}
    </span>
  )
}
